import { ApolloClient } from '@apollo/client'

import { env } from 'src/env/client.mjs'
import { GENERATE_PRESIGNED_URL } from 'src/graphql/queries/s3.queries'
import { AssetRef, S3Asset } from 'src/types/form'

import { formatDate } from './date'

async function getPresignedFileUrlFromS3(
  queryFn: ApolloClient<object>['query'],
  asset: AssetRef,
  isPublic?: boolean
): Promise<S3Asset> {
  const assetUrl = isPublic
    ? `${env.NEXT_PUBLIC_ASSETS_DOMAIN}/${asset.Uri}`
    : ''

  if (!assetUrl) {
    const { data } = await queryFn({
      query: GENERATE_PRESIGNED_URL,
      variables: { path: asset.Uri }
    })
    return {
      Id: asset.Id!,
      FileName: asset.FileName,
      Uri: data.presignedUrl
    } as S3Asset
  }
  return {
    Id: asset.Id,
    Uri: assetUrl,
    FileName: asset.FileName
  } as S3Asset
}

export async function getPresignedFileUrlsFromS3(
  queryFn: ApolloClient<object>['query'],
  assets: AssetRef[],
  isPublic?: boolean
) {
  return Promise.all(
    assets.map(a => getPresignedFileUrlFromS3(queryFn, a, isPublic))
  )
}

const isS3Asset = (file: File | S3Asset): file is S3Asset => {
  return 'Id' in file
}

const isSameFile = (asset1: File | S3Asset, asset2: File | S3Asset) => {
  if (isS3Asset(asset1) && isS3Asset(asset2)) {
    return asset1.Id === asset2.Id
  }
  return asset1.name === asset2.name
}

export function buildAssetsDiff(
  assets1: (File | S3Asset)[],
  assets2: (File | S3Asset)[]
) {
  return assets1.filter(a1 => {
    return !assets2.find(a2 => isSameFile(a1, a2))
  })
}

export type MappedPhotos = {
  plantings?: { title: string; photos: (S3Asset | undefined)[] }[]
  monitorings?: {
    title: string
    photos: (S3Asset | undefined)[]
  }[]
  project?: (S3Asset | undefined)[]
  cover?: string
}

export type PlantingAssetsData = {
  name: string
  plantingAssets: {
    uri: string
    id: string
  }[]
  monitoringEvents: {
    monitoredOn: string
    monitoringAssets: {
      uri: string
      id: string
    }[]
  }[]
}[]

export const photosForPreview = (photos: MappedPhotos) => {
  const finalArray: Array<string | undefined> = [photos.cover]

  if (photos.monitorings) {
    finalArray.push(
      ...photos.monitorings.flatMap(m => m.photos.map(p => p?.Uri))
    )
  }

  if (photos.plantings) {
    finalArray.push(
      ...photos.plantings.flatMap(p => p.photos.map(pl => pl?.Uri))
    )
  }

  if (photos.project) {
    finalArray.push(...photos.project.map(p => p?.Uri))
  }

  return finalArray.filter(p => typeof p === 'string') as Array<string>
}

export async function mapPhotos(
  client: ApolloClient<object>,
  plantingData: PlantingAssetsData
): Promise<MappedPhotos> {
  const plantingAssets = plantingData.flatMap(pd =>
    pd.plantingAssets.filter(a => !!a.uri).map(a => ({ Id: a.id, Uri: a.uri! }))
  )

  const monitoringAssets = plantingData.flatMap(pd =>
    pd.monitoringEvents.flatMap(m =>
      m.monitoringAssets
        .filter(a => !!a.uri)
        .map(ma => ({
          Id: ma.id,
          Uri: ma.uri!
        }))
    )
  )

  const filesFromS3 = await getPresignedFileUrlsFromS3(client.query, [
    ...plantingAssets,
    ...monitoringAssets
  ])

  const plantings: MappedPhotos['plantings'] = plantingData.map(pd => ({
    title: pd.name,
    photos: filesFromS3.filter(
      f => pd.plantingAssets.findIndex(pa => pa.id === f?.Id) !== -1
    )
  }))

  const monitorings: MappedPhotos['monitorings'] = plantingData.flatMap(pd =>
    pd.monitoringEvents.map(me => {
      return {
        title: formatDate({
          date: me.monitoredOn,
          format: 'DD MMM YYYY',
          isUtc: true
        })!,
        photos: filesFromS3.filter(
          f => me.monitoringAssets.findIndex(ma => ma.id === f?.Id) !== -1
        )
      }
    })
  )

  return {
    plantings,
    monitorings: monitorings.filter(m => m.photos.length)
  }
}
